import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', grey: '#6B7280',
  border: '#E5E7EB', success: '#10B981', blue: '#3B82F6',
  purple: '#8B5CF6', red: '#EF4444', orange: '#F59E0B',
  cyan: '#06B6D4', indigo: '#6366F1',
};

const TIERS = [
  {
    key: 'ai',
    icon: '🤖',
    label: 'AI Practice Only',
    sublabel: 'Self-paced · No human tutor · Supervised at your centre',
    desc: 'Structured AI practice sessions at the learning centre. No scheduled tutor classes — students work through the platform at their own pace. Sold as a fixed access period, not monthly.',
    color: C.teal,
  },
  {
    key: 'online',
    icon: '🖥️',
    label: 'Online Tutor Classes',
    sublabel: 'Live video · Qualified tutors · For students abroad or remote',
    desc: 'Live video sessions with a real tutor. Same CEFR roadmap as physical. For students who cannot attend in person.',
    color: C.cyan,
  },
  {
    key: 'physical',
    icon: '🏫',
    label: 'Physical Centre',
    sublabel: 'In-person · At your learning centre',
    desc: 'Attend the learning centre in person. CEFR-structured classes, supervised AI practice daily, and tutor-led sessions. Pay per level.',
    color: C.success,
  },
];

const PACKAGES = [
  // ── AI Only ──
  {
    id: 'ai_basic', tier: 'ai', icon: '🤖',
    name: 'AI Practice Only', subtitle: 'Structured AI practice — no tutor required',
    price: '$39', period: '/mo', upfront: '$398 for 12 months', badge: null, color: C.teal,
    features: [
      'Up to 1.5 hours of supervised AI practice per day',
      'All 7 skills: Grammar · Vocabulary · Reading · Writing · Conversation · IELTS · Business',
      'CEFR level assessment + progress tracking',
      'Personal vocabulary bank',
      'Level certificates at each stage',
      'Weekly progress report',
    ],
  },
  {
    id: 'ai_ielts', tier: 'ai', icon: '🎯',
    name: 'AI IELTS Prep', subtitle: 'Intensive IELTS practice without a tutor',
    price: '$69', period: '/mo', upfront: '$352 for 6 months', badge: null, color: C.blue,
    features: [
      'Up to 1.5 hours of IELTS-focused AI practice per day',
      'Writing tasks graded on official IELTS band descriptors',
      'All 4 IELTS skills covered daily',
      'Timed reading practice — real exam simulation',
      'Full mock tests with band score estimates',
      '8–12 week structured prep plan',
      'IELTS Preparation Certificate on completion',
    ],
  },
  {
    id: 'ai_business', tier: 'ai', icon: '💼',
    name: 'AI Business English', subtitle: 'Professional English AI practice',
    price: '$69', period: '/mo', upfront: '$352 for 6 months', badge: null, color: C.grey,
    features: [
      'Up to 1.5 hours of Business English AI practice per day',
      'Professional email writing with AI feedback',
      'Meeting language, presentations and negotiations',
      'Business vocabulary by industry sector',
      'Corporate communication scenarios',
      'Report and proposal writing practice',
      'Progress tracking and completion certificates',
    ],
  },
  // ── Online Tutor ──
  {
    id: 'online_group', tier: 'online', icon: '🖥️',
    name: 'Online Group Classes', subtitle: 'Live group lessons via video call',
    price: '$59', period: '/mo', upfront: null, badge: null, color: C.cyan,
    features: [
      '12 × live group video sessions/month (90 min, 3×/week)',
      'Up to 12 students per group — CEFR level matched',
      'Taught by a qualified tutor via video call',
      'CEFR level tracking — structured A1 to C2 roadmap',
      'AI practice platform included between sessions',
      'Level exams and certificates at each stage',
      'Pay per level — monthly or upfront (10% off)',
    ],
  },
  {
    id: 'online_standard', tier: 'online', icon: '⭐',
    name: 'Online Standard', subtitle: 'Group video classes + daily AI practice',
    price: '$85', period: '/mo', upfront: null, badge: 'Most Popular Online', color: C.indigo,
    features: [
      '12 × live group video sessions/month (90 min, 3×/week)',
      'Up to 12 students per group — CEFR level matched',
      'Daily AI practice platform — all 7 skill types',
      'Tutor reviews AI progress before every class',
      'Personal vocabulary bank and progress tracking',
      'CEFR assessments and level certificates',
      'Pay per level — monthly or upfront (10% off)',
    ],
  },
  {
    id: 'online_1on1', tier: 'online', icon: '💎',
    name: 'Online Premium Group', subtitle: 'Premium group video classes with AI coaching',
    price: '$129', period: '/mo', upfront: null, badge: null, color: C.purple,
    features: [
      '12 × premium group video sessions/month (90 min, 3×/week)',
      'Up to 12 students per group — CEFR level matched',
      'Tutor tailors every session to the group\'s pace and goals',
      'Daily AI practice platform included',
      'Tutor reviews full AI progress before each class',
      'CEFR assessments, certificates and full roadmap',
      'Pay per level — monthly or upfront (10% off)',
    ],
  },
  {
    id: 'online_ielts', tier: 'online', icon: '🏆',
    name: 'Online IELTS Group', subtitle: 'Fast-track IELTS prep via group video',
    price: '$199', period: '/mo', upfront: null, badge: 'Best for IELTS', color: C.red,
    features: [
      '20 × live IELTS group sessions/month (90 min, 5×/week)',
      'Up to 12 students per group — IELTS level matched',
      'Taught by an IELTS-qualified specialist via video call',
      'Writing tasks reviewed and graded every class',
      'Speaking practice with detailed feedback each session',
      'Full timed mock test every 2 weeks',
      'Band score tracking week by week',
    ],
  },
  // ── Physical Centre ──
  {
    id: 'starter_group', tier: 'physical', icon: '👥',
    name: 'Group Classes', subtitle: 'Learn together at your level',
    price: '$59', period: '/mo', upfront: null, badge: null, color: C.success,
    features: [
      '12 × group classes/month at your centre (90 min, 3×/week)',
      'Up to 12 students per group — same CEFR level',
      'Taught by a qualified tutor',
      'AI Practice platform available between classes',
      'Tutor reviews AI progress before every class',
      'CEFR tracking, certificates and full roadmap',
      'Pay per level — monthly or upfront (10% off)',
    ],
  },
  {
    id: 'standard', tier: 'physical', icon: '⭐',
    name: 'Standard', subtitle: 'The complete school experience',
    price: '$99', period: '/mo', upfront: null, badge: 'Most Popular', color: C.navy,
    features: [
      '12 × group classes/month at your centre (90 min, 3×/week)',
      'Up to 12 students per group — CEFR level matched',
      'Up to 2 hours of supervised AI practice per day',
      'Tutor sees full AI history — every class personalised',
      'All 7 AI practice types included',
      'Personal vocabulary bank and weekly progress report',
      'CEFR assessments, certificates and full roadmap',
    ],
  },
  {
    id: 'premium', tier: 'physical', icon: '💎',
    name: 'Premium Group', subtitle: 'Premium group coaching for fastest results',
    price: '$159', period: '/mo', upfront: null, badge: null, color: C.purple,
    features: [
      '12 × premium group sessions/month at your centre (90 min, 3×/week)',
      'Up to 12 students per group — CEFR level matched',
      'Tutor tailors every class to the group\'s pace and goals',
      'Up to 2 hours of supervised AI practice per day',
      'Tutor reviews all AI practice before each class',
      'CEFR assessments, certificates and full roadmap',
      'Pay per level — monthly or upfront (10% off)',
    ],
  },
  {
    id: 'ielts_intensive', tier: 'physical', icon: '🏆',
    name: 'IELTS Group Intensive', subtitle: 'Fastest route to your target band score',
    price: '$249', period: '/mo', upfront: null, badge: 'Best for IELTS', color: C.red,
    features: [
      '20 × IELTS group sessions/month at your centre (90 min, 5×/week)',
      'Up to 12 students per group — IELTS band matched',
      'Taught by an IELTS-qualified specialist tutor',
      'Writing tasks reviewed and graded in every class',
      'Speaking practice with detailed feedback each session',
      'Full timed mock test every 2 weeks',
      'Band score tracking — see improvement week by week',
    ],
  },
  {
    id: 'corporate', tier: 'physical', icon: '🏢',
    name: 'Corporate Group', subtitle: 'Executive English for professionals',
    price: '$299', period: '/mo', upfront: null, badge: null, color: C.orange,
    features: [
      '12 × Business English group sessions/month (90 min, 3×/week)',
      'Up to 12 students per group — industry and level matched',
      'Classes built around your sector, role and goals',
      'Presentation coaching, business writing and negotiations',
      'Meeting language, emails, reports and proposals',
      'Up to 2 hours of Business English AI practice per day',
      'Corporate group completion certificate',
    ],
  },
];

export default function AdminPricing() {
  const [tenant, setTenant] = useState(null);
  const [activeTier, setActiveTier] = useState('ai');

  useEffect(() => {
    api.get('/api/admin/dashboard').then(r => setTenant(r.data?.tenant)).catch(() => {});
  }, []);

  const visiblePackages = PACKAGES.filter(p => p.tier === activeTier);

  return (
    <Layout role="tenant_admin" tenantColor={tenant?.primary_colour} tenantName={tenant?.school_name}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: C.navy }}>Pricing & Packages</h2>
        <p style={{ margin: 0, color: C.grey, fontSize: 14 }}>SpeakIQ standard packages — all prices in USD</p>
      </div>

      {/* Tier tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
        {TIERS.map(t => (
          <button key={t.key} onClick={() => setActiveTier(t.key)}
            style={{
              padding: '10px 18px', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13,
              background: activeTier === t.key ? t.color : '#F1F5F9',
              color: activeTier === t.key ? '#fff' : C.grey,
              transition: 'all 0.15s',
            }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tier description */}
      {TIERS.filter(t => t.key === activeTier).map(t => (
        <div key={t.key} style={{ background: '#F8FAFC', border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 18px', marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.navy, marginBottom: 4 }}>{t.icon} {t.label} <span style={{ fontWeight: 400, color: C.grey, fontSize: 13 }}>— {t.sublabel}</span></div>
          <div style={{ fontSize: 13, color: C.grey }}>{t.desc}</div>
        </div>
      ))}

      {/* Package cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20, marginBottom: 24 }}>
        {visiblePackages.map(pkg => (
          <div key={pkg.id} style={{
            background: '#fff', borderRadius: 16, padding: 24,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            border: pkg.badge ? `2px solid ${pkg.color}` : `1px solid ${C.border}`,
            position: 'relative',
          }}>
            {pkg.badge && (
              <div style={{
                position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                background: pkg.color, color: '#fff', padding: '3px 14px',
                borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
              }}>
                {pkg.badge}
              </div>
            )}
            <div style={{ fontSize: 28, marginBottom: 8 }}>{pkg.icon}</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: C.navy, marginBottom: 2 }}>{pkg.name}</div>
            <div style={{ fontSize: 12, color: C.grey, marginBottom: 12 }}>{pkg.subtitle}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: pkg.badge ? pkg.color : C.navy, marginBottom: 2, lineHeight: 1 }}>
              {pkg.price}<span style={{ fontSize: 14, fontWeight: 400, color: C.grey }}>{pkg.period}</span>
            </div>
            {pkg.upfront && (
              <div style={{ fontSize: 12, color: pkg.color, fontWeight: 600, marginBottom: 14 }}>{pkg.upfront}</div>
            )}
            {!pkg.upfront && <div style={{ marginBottom: 14 }} />}
            {pkg.features.map((f, j) => (
              <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 7, fontSize: 13, color: C.grey }}>
                <span style={{ color: C.success, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
                {f}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '14px 16px', fontSize: 13, color: '#1D4ED8' }}>
        <strong>ℹ️ Note:</strong> These are SpeakIQ standard packages. Tier 2 partners can configure custom pricing. Contact your account manager to discuss custom packages.
      </div>
    </Layout>
  );
}
